import { IconButton, Menu, MenuItem, ListItemIcon } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DehazeIcon from "@material-ui/icons/Dehaze";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SettingsIcon from "@material-ui/icons/Settings";
import { AUTH_SIGNOUT } from "auth/AuthorizationTypes";
import { useEmail } from "auth/useEmail";
import { useDashboardPeriod } from "dashboards/useDashboardPeriod";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { AppState } from "redux/AppTypes";
import { tokens } from "./theme";

const useStyles = makeStyles({
  root: {
    height: 56,
    flex: "none",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "0 18px",
    borderBottom: `1px solid ${tokens.hairline}`,
    background: "rgba(15,18,25,0.5)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },
  crumb: {
    font: `400 12px/1 ${tokens.fontMono}`,
    color: tokens.fg3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  period: {
    display: "flex",
    alignItems: "center",
    marginLeft: 6,
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${tokens.hairlineStrong}`,
    borderRadius: 6,
    padding: 2,
    flex: "none",
  },
  periodLabel: {
    font: `500 12px/1 ${tokens.fontMono}`,
    color: tokens.fg1,
    minWidth: 70,
    textAlign: "center",
  },
  iconButton: {
    width: 24,
    height: 24,
    padding: 0,
    color: tokens.fg2,
    borderRadius: 4,
  },
  squareButton: {
    width: 32,
    height: 32,
    background: "rgba(255,255,255,0.04)",
    color: tokens.fg2,
    border: `1px solid ${tokens.hairlineStrong}`,
    borderRadius: 6,
  },
  live: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    marginLeft: 2,
    flex: "none",
  },
});

export function TopBar() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const email = useEmail();
  const { year, month } = useDashboardPeriod();
  const alive = useSelector((state: AppState) => state.websocket.alive);
  const [menuEl, setMenuEl] = useState(null as any);

  const crumb = ["ops"]
    .concat(location.pathname.split("/").filter(Boolean))
    .join(" / ")
    .toLowerCase();

  const setPeriod = (newYear: number, newMonth: number) => {
    const date = new Date(newYear, newMonth);
    history.push({
      search: `year=${date.getFullYear()}&month=${date.getMonth() + 1}`,
    });
  };

  const periodLabel = new Date(year, month).toLocaleString("default", {
    month: "short",
    year: "numeric",
  });

  const navigateFromMenu = (path: string) => {
    setMenuEl(null);
    history.push(path);
  };

  return (
    <div className={classes.root}>
      <div className={classes.crumb}>{crumb}</div>
      <div className={classes.period}>
        <IconButton
          title="Previous period"
          className={classes.iconButton}
          onClick={() => setPeriod(year, month - 1)}
        >
          <ChevronLeftIcon style={{ fontSize: 16 }} />
        </IconButton>
        <span className={classes.periodLabel}>{periodLabel}</span>
        <IconButton
          title="Next period"
          className={classes.iconButton}
          onClick={() => setPeriod(year, month + 1)}
        >
          <ChevronRightIcon style={{ fontSize: 16 }} />
        </IconButton>
      </div>
      <div style={{ flex: 1 }} />
      <IconButton
        aria-label="more"
        className={classes.squareButton}
        onClick={(e) => setMenuEl(e.currentTarget)}
      >
        <DehazeIcon style={{ fontSize: 16 }} />
      </IconButton>
      <Menu
        anchorEl={menuEl}
        keepMounted
        open={Boolean(menuEl)}
        onClose={() => setMenuEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem onClick={() => navigateFromMenu("/administration")}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          Administration
        </MenuItem>
        <MenuItem onClick={() => dispatch({ type: AUTH_SIGNOUT })}>
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          Logout ({email})
        </MenuItem>
      </Menu>
      <span
        className={classes.live}
        title={alive ? "Realtime connection active" : "Connection lost — click to reload"}
        onClick={alive ? undefined : () => window.location.reload()}
        style={alive ? undefined : { cursor: "pointer" }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: alive ? tokens.ok : tokens.crit,
            boxShadow: alive
              ? "0 0 12px hsla(142, 64%, 48%, 0.4)"
              : "0 0 12px hsla(0, 72%, 58%, 0.55)",
            animation: "dotpulse 2s ease-in-out infinite",
          }}
        />
        <span style={{ font: `500 12px/1 ${tokens.fontMono}`, color: tokens.fg2 }}>
          {alive ? "live" : "offline — reload"}
        </span>
      </span>
    </div>
  );
}
